import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { dataflowApi, type TemplateUpsertPayload } from "@/services/api/dataflow";
import { useDataFlowStore } from "@/stores/dataflowStore";
import { TaskTypeSelect } from "@/components/dataflow/TaskTypeBadge";
import { StockPoolConfig, type StockPoolType } from "@/components/dataflow/StockPoolConfig";
import { DateRangeConfig, type DateRangeType } from "@/components/dataflow/DateRangeConfig";
import type { Template } from "@/types/dataflow";


const INDEX_GROUP_OPTIONS = [
  { value: "hs300", label: "A300 / 沪深300" },
  { value: "zz500", label: "中证500" },
  { value: "zz1000", label: "中证1000" },
  { value: "zz2000", label: "中证2000" },
] as const;

const WEEKDAY_OPTIONS = [
  { value: 1, label: "周一" },
  { value: 2, label: "周二" },
  { value: 3, label: "周三" },
  { value: 4, label: "周四" },
  { value: 5, label: "周五" },
  { value: 6, label: "周六" },
  { value: 7, label: "周日" },
] as const;

const EMPTY_TEMPLATE: Partial<Template> = {
  name: "",
  task_type: "daily_sync",
  stock_pool_type: "all_a",
  stock_pool_params: {},
  date_range_type: "today",
  date_range_params: {},
  options_json: {},
  depends_on_template_ids: [],
  schedule_kind: "",
  schedule_config_json: {
    time: "15:05",
    trade_day_only: true,
    weekday: 1,
    monthday: 1,
  },
  status: "ACTIVE",
  inherit_upstream_scope: false,
};

function normalizeTime(raw: unknown): string {
  const text = String(raw || "").trim();
  const match = text.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) return "15:05";
  const hh = Math.max(0, Math.min(23, Number(match[1])));
  const mm = Math.max(0, Math.min(59, Number(match[2])));
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function parseHm(time: string): { hh: number; mm: number } {
  const normalized = normalizeTime(time);
  const [hhText, mmText] = normalized.split(":");
  return { hh: Number(hhText), mm: Number(mmText) };
}

function buildWeeklyCron(time: string, weekday: number): string {
  const { hh, mm } = parseHm(time);
  const cronDow = weekday === 7 ? 0 : weekday;
  return `${mm} ${hh} * * ${cronDow}`;
}

function buildMonthlyCron(time: string, monthday: number): string {
  const { hh, mm } = parseHm(time);
  const day = Math.max(1, Math.min(31, Number(monthday || 1)));
  return `${mm} ${hh} ${day} * *`;
}

function normalizeSymbol(raw: unknown): string {
  let text = String(raw || "").trim().toUpperCase();
  if (!text) return "";
  if (!text.includes(".") && /^\d{6}$/.test(text)) {
    const suffix = text.startsWith("6") || text.startsWith("5") || text.startsWith("9") ? "SH" : "SZ";
    text = `${text}.${suffix}`;
  }
  return text;
}

function normalizeSymbolList(raw: unknown): string[] {
  let values: string[] = [];
  if (Array.isArray(raw)) {
    values = raw.map((item) => String(item || ""));
  } else if (typeof raw === "string") {
    values = raw.replace(/\n/g, ",").split(",");
  } else {
    values = [String(raw || "")];
  }
  return values.map(normalizeSymbol).filter(Boolean);
}

function normalizeStockPoolFromTemplate(raw: Partial<Template> | undefined): {
  type: StockPoolType;
  params: Record<string, unknown>;
} {
  const poolType = (raw?.stock_pool_type || "all_a") as StockPoolType;
  const poolParams = (raw?.stock_pool_params || {}) as Record<string, unknown>;
  return { type: poolType, params: poolParams };
}

function formatIsoDate(value: Date): string {
  const year = value.getUTCFullYear();
  const month = String(value.getUTCMonth() + 1).padStart(2, "0");
  const day = String(value.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseWeekRange(weekValue: string): { start_date: string; end_date: string } | null {
  const match = String(weekValue || "").match(/^(\d{4})-W(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const week = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(week) || week < 1 || week > 53) {
    return null;
  }
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Weekday = jan4.getUTCDay() || 7;
  const week1Monday = new Date(Date.UTC(year, 0, 4 - (jan4Weekday - 1)));
  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return {
    start_date: formatIsoDate(monday),
    end_date: formatIsoDate(sunday),
  };
}

function parseMonthRange(monthValue: string): { start_date: string; end_date: string } | null {
  const match = String(monthValue || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return null;
  }
  const start = new Date(Date.UTC(year, month - 1, 1));
  const nextMonth = month === 12 ? new Date(Date.UTC(year + 1, 0, 1)) : new Date(Date.UTC(year, month, 1));
  const end = new Date(nextMonth);
  end.setUTCDate(nextMonth.getUTCDate() - 1);
  return {
    start_date: formatIsoDate(start),
    end_date: formatIsoDate(end),
  };
}

function parseYearRange(yearValue: string): { start_date: string; end_date: string } | null {
  const year = Number(String(yearValue || "").trim());
  if (!Number.isFinite(year) || year < 1990 || year > 2099) {
    return null;
  }
  return {
    start_date: `${year}-01-01`,
    end_date: `${year}-12-31`,
  };
}

function normalizeScheduleConfig(
  scheduleKind: string | undefined,
  rawCfg: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const kind = String(scheduleKind || "").trim().toLowerCase();
  const cfg = { ...(rawCfg || {}) };
  const time = normalizeTime(cfg.time);
  cfg.time = time;

  if (kind === "daily") {
    cfg.trade_day_only = cfg.trade_day_only !== false;
    delete cfg.cron;
    return cfg;
  }

  if (kind === "weekly") {
    const weeklyList = Array.isArray(cfg.weekdays) ? cfg.weekdays : [];
    const weekday = Math.max(1, Math.min(7, Number(cfg.weekday || weeklyList[0] || 1)));
    cfg.weekday = weekday;
    cfg.weekdays = [weekday];
    cfg.cron = String(cfg.cron || "").trim() || buildWeeklyCron(time, weekday);
    return cfg;
  }

  if (kind === "monthly") {
    const monthdayList = Array.isArray(cfg.monthdays) ? cfg.monthdays : [];
    const monthday = Math.max(1, Math.min(31, Number(cfg.monthday || monthdayList[0] || 1)));
    cfg.monthday = monthday;
    cfg.monthdays = [monthday];
    cfg.cron = String(cfg.cron || "").trim() || buildMonthlyCron(time, monthday);
    return cfg;
  }

  return {};
}

export function TemplateConfig() {
  const { selectedTemplateId, setSelectedTemplate } = useDataFlowStore();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Partial<Template>>(EMPTY_TEMPLATE);
  const [showAddUpstream, setShowAddUpstream] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [actionError, setActionError] = useState("");

  const isDraft = selectedTemplateId === "new";
  const numericId = !isDraft && selectedTemplateId ? Number(selectedTemplateId) : null;

  const { data: template, isLoading, error: templateError } = useQuery({
    queryKey: ["dataflow", "templates", numericId],
    queryFn: () => dataflowApi.getTemplate(String(numericId)),
    enabled: numericId != null && Number.isFinite(numericId),
  });

  const { data: allTemplates } = useQuery({
    queryKey: ["dataflow", "templates"],
    queryFn: () => dataflowApi.getTemplates(),
  });

  const updateMutation = useMutation({
    mutationFn: (data: TemplateUpsertPayload) => dataflowApi.updateTemplate(String(numericId), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dataflow", "templates", numericId] });
      queryClient.invalidateQueries({ queryKey: ["dataflow", "templates"] });
      setActionError("");
      setSavedMsg("Saved");
      setTimeout(() => setSavedMsg(""), 1500);
    },
    onError: (error: Error) => {
      setActionError(error.message || "Save failed");
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: TemplateUpsertPayload) => dataflowApi.createTemplate(data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["dataflow", "templates"] });
      setSelectedTemplate(String(created.id));
      sessionStorage.removeItem("dataflow:new-template-name");
      setActionError("");
      setSavedMsg("Created");
      setTimeout(() => setSavedMsg(""), 1500);
    },
    onError: (error: Error) => {
      setActionError(error.message || "Create failed");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => dataflowApi.deleteTemplate(String(numericId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dataflow", "templates"] });
      setSelectedTemplate(null);
      setActionError("");
      setSavedMsg("Deleted");
      setTimeout(() => setSavedMsg(""), 1200);
    },
    onError: (error: Error) => {
      setActionError(error.message || "Delete failed");
    },
  });

  const runMutation = useMutation({
    mutationFn: () => dataflowApi.runTemplate(String(numericId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dataflow", "instances"] });
      queryClient.invalidateQueries({ queryKey: ["dataflow", "batches"] });
      setActionError("");
      setSavedMsg("Running");
      setTimeout(() => setSavedMsg(""), 1500);
    },
    onError: (error: Error) => {
      setActionError(error.message || "Run failed");
    },
  });

  const templateFromList = numericId != null ? allTemplates?.find((item) => item.id === numericId) : undefined;
  const resolvedTemplate = template ?? templateFromList;

  useEffect(() => {
    if (resolvedTemplate) {
      const scheduleKind = String(resolvedTemplate.schedule_kind || "");
      const scheduleConfig = normalizeScheduleConfig(
        scheduleKind,
        (resolvedTemplate.schedule_config_json || {}) as Record<string, unknown>,
      );
      setForm({
        ...EMPTY_TEMPLATE,
        ...resolvedTemplate,
        schedule_kind: scheduleKind,
        schedule_config_json: scheduleConfig,
      });
      setActionError("");
      return;
    }

    if (isDraft) {
      const hintedName = sessionStorage.getItem("dataflow:new-template-name") || "";
      setForm({
        ...EMPTY_TEMPLATE,
        name: hintedName || "",
      });
      setActionError("");
      return;
    }

    setForm(EMPTY_TEMPLATE);
    setActionError("");
  }, [resolvedTemplate, isDraft]);

  const updateForm = <K extends keyof Template>(key: K, value: Template[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateScheduleConfig = (key: string, value: unknown) => {
    setForm((prev) => {
      const next = {
        ...(prev.schedule_config_json || {}),
        [key]: value,
      };
      const kind = String(prev.schedule_kind || "").toLowerCase();
      const time = normalizeTime(next.time);
      if (kind === "weekly" && (key === "time" || key === "weekday")) {
        next.time = time;
        next.cron = buildWeeklyCron(time, Number(next.weekday || 1));
      }
      if (kind === "monthly" && (key === "time" || key === "monthday")) {
        next.time = time;
        next.cron = buildMonthlyCron(time, Number(next.monthday || 1));
      }
      return {
        ...prev,
        schedule_config_json: next,
      };
    });
  };

  const updateOptions = (key: string, value: unknown) => {
    setForm((prev) => ({
      ...prev,
      options_json: {
        ...(prev.options_json || {}),
        [key]: value,
      },
    }));
  };

  const addUpstream = (id: number) => {
    const ids = form.depends_on_template_ids || [];
    if (!ids.includes(id)) {
      updateForm("depends_on_template_ids", [...ids, id]);
    }
    setShowAddUpstream(false);
  };

  const removeUpstream = (id: number) => {
    const ids = form.depends_on_template_ids || [];
    updateForm(
      "depends_on_template_ids",
      ids.filter((value) => value !== id),
    );
  };

  const handleSave = () => {
    const name = String(form.name || "").trim();
    if (!name) {
      setActionError("Template name is required");
      return;
    }
    const payload: TemplateUpsertPayload = {
      name,
      task_type: String(form.task_type || "daily_sync"),
      stock_pool_type: (form.stock_pool_type || "all_a") as TemplateUpsertPayload["stock_pool_type"],
      stock_pool_params: (form.stock_pool_params || {}) as Record<string, unknown>,
      date_range_type: (form.date_range_type || "today") as TemplateUpsertPayload["date_range_type"],
      date_range_params: (form.date_range_params || {}) as Record<string, unknown>,
      options_json: (form.options_json || {}) as Record<string, unknown>,
      depends_on_template_ids: (form.depends_on_template_ids || []).map(Number).filter(Number.isFinite),
      schedule_kind: String(form.schedule_kind || "").trim() || undefined,
      schedule_config_json: normalizeScheduleConfig(
        form.schedule_kind,
        (form.schedule_config_json || {}) as Record<string, unknown>,
      ) as TemplateUpsertPayload["schedule_config_json"],
      inherit_upstream_scope: canToggleInheritScope && form.inherit_upstream_scope === true,
    };
    setActionError("");
    if (isDraft) {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate(payload);
    }
  };

  const handleRun = () => {
    if (isDraft || numericId == null || !Number.isFinite(numericId)) {
      setActionError("Please save template first");
      return;
    }
    setActionError("");
    runMutation.mutate();
  };

  const handleDelete = () => {
    if (isDraft || numericId == null || !Number.isFinite(numericId)) return;
    if (!confirm(`确定要删除模板 "${form.name || numericId}" 吗？`)) return;
    setActionError("");
    deleteMutation.mutate();
  };

  const isDailySync = String(form.task_type || "").toLowerCase() === "daily_sync";
  const stockPoolType = (form.stock_pool_type || "all_a") as StockPoolType;
  const stockPoolParams = (form.stock_pool_params || {}) as Record<string, unknown>;
  const upstreamDepIds = (form.depends_on_template_ids || []).map(Number).filter(Number.isFinite);
  const primaryUpstreamTemplate = upstreamDepIds.length > 0
    ? (allTemplates || []).find((item) => item.id === upstreamDepIds[0])
    : undefined;
  const hasUpstreamConfigured = Boolean(primaryUpstreamTemplate);
  const canToggleInheritScope = hasUpstreamConfigured;
  const inheritUpstreamScope = canToggleInheritScope && form.inherit_upstream_scope === true;
  const primaryUpstreamPool = normalizeStockPoolFromTemplate(primaryUpstreamTemplate);
  const dateRangeType = (form.date_range_type || "today") as DateRangeType;
  const dateRangeParams = (form.date_range_params || {}) as Record<string, unknown>;
  const scheduleKind = String(form.schedule_kind || "").toLowerCase();
  const scheduleConfig = (form.schedule_config_json || {}) as Record<string, unknown>;
  const isSaving = updateMutation.isPending || createMutation.isPending;
  const isDeleting = deleteMutation.isPending;
  const isRunning = runMutation.isPending;
  const isBusy = isSaving || isDeleting || isRunning;

  useEffect(() => {
    if (canToggleInheritScope) return;
    setForm((prev) => (prev.inherit_upstream_scope === false ? prev : { ...prev, inherit_upstream_scope: false }));
  }, [canToggleInheritScope]);

  useEffect(() => {
    if (!inheritUpstreamScope || !primaryUpstreamTemplate) return;
    setForm((prev) => {
      const nextType = primaryUpstreamPool.type;
      const nextParams = primaryUpstreamPool.params;
      const prevType = (prev.stock_pool_type || "all_a") as StockPoolType;
      const prevParams = (prev.stock_pool_params || {}) as Record<string, unknown>;
      if (prevType === nextType && JSON.stringify(prevParams) === JSON.stringify(nextParams)) {
        return prev;
      }
      return {
        ...prev,
        stock_pool_type: nextType,
        stock_pool_params: nextParams as Template["stock_pool_params"],
      };
    });
  }, [
    inheritUpstreamScope,
    primaryUpstreamTemplate?.id,
    primaryUpstreamTemplate?.stock_pool_type,
    JSON.stringify(primaryUpstreamTemplate?.stock_pool_params || {}),
  ]);

  if (!selectedTemplateId) {
    return <div className="h-full flex items-center justify-center text-[#64748b] text-sm">Select a template to configure</div>;
  }

  if (isLoading && !templateFromList) {
    return <div className="h-full flex items-center justify-center text-[#64748b] text-sm">Loading...</div>;
  }

  return (
    <div className="h-full overflow-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-[#f8fafc] font-semibold">{form.name || "New Template"}</h2>
          <p className="text-[#64748b] text-xs">
            ID: {isDraft ? "draft" : selectedTemplateId}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {savedMsg && <span className="text-[#22c55e] text-xs">{savedMsg}</span>}
          {!isDraft && (
            <button
              onClick={handleDelete}
              disabled={isDeleting || isSaving || isRunning}
              className="px-3 py-1.5 bg-[#7f1d1d] text-white text-xs rounded hover:bg-[#991b1b] disabled:opacity-50 inline-flex items-center gap-1"
              title="删除模板"
            >
              <Trash2 className="size-3" />
              Delete
            </button>
          )}
          <button
            onClick={handleRun}
            disabled={isDraft || isBusy}
            className="px-3 py-1.5 bg-[#16a34a] text-white text-xs rounded hover:bg-[#15803d] disabled:opacity-50"
          >
            Run Now
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isDeleting}
            className="px-3 py-1.5 bg-[#0ea5e9] text-white text-xs rounded hover:bg-[#0284c7] disabled:opacity-50"
          >
            {isDraft ? "Create" : "Save"}
          </button>
        </div>
      </div>

      {actionError && (
        <div className="mb-3 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
          {actionError}
        </div>
      )}

      <div className="space-y-4">
        {templateError && templateFromList && (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">
            Detail refresh failed. Using template data from the list view.
          </div>
        )}

        <div className="bg-[#0f1115] border border-[var(--border)] rounded p-4">
          <h3 className="text-[#64748b] text-[10px] uppercase mb-3">Basic Settings</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[#64748b] text-[10px] block mb-1">Template Name</label>
              <input
                type="text"
                value={form.name || ""}
                onChange={(e) => updateForm("name", e.target.value)}
                className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
              />
            </div>
            <div>
              <label className="text-[#64748b] text-[10px] block mb-1">Task Type</label>
              <TaskTypeSelect
                value={form.task_type || "daily_sync"}
                onChange={(value) => updateForm("task_type", value)}
                className="w-full"
              />
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-[var(--border)]">
            <h3 className="text-[#64748b] text-[10px] uppercase mb-3">Trigger: Upstream (OR)</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {(form.depends_on_template_ids || []).map((id) => {
                const dep = allTemplates?.find((item) => item.id === id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-[#1e3a5f] border border-[#3b82f6] rounded text-[#7dd3fc] text-xs"
                  >
                    {dep?.name || id}
                    <button onClick={() => removeUpstream(id)} className="text-[#3b82f6] hover:text-[#7dd3fc]">
                      ×
                    </button>
                  </span>
                );
              })}
              <button
                onClick={() => setShowAddUpstream(!showAddUpstream)}
                className="px-2 py-1 border border-dashed border-[#475569] text-[#64748b] rounded text-xs hover:border-[#94a3b8] hover:text-[#94a3b8]"
              >
                + Add
              </button>
            </div>

            {showAddUpstream && (
              <div className="bg-[#1a1d24] border border-[var(--border)] rounded p-2 mb-3">
                <select
                  onChange={(e) => addUpstream(Number(e.target.value))}
                  className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select template...
                  </option>
                  {(allTemplates || [])
                    .filter((item) => item.id !== numericId && !(form.depends_on_template_ids || []).includes(item.id))
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <p className="text-[#64748b] text-[10px]">
              Upstream trigger and schedule trigger are OR-related: either schedule hit OR upstream completion can start this template.
            </p>
          </div>

          {/* Unified StockPool Configuration */}
          <div className="mt-4 pt-4 border-t border-[var(--border)]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#64748b] text-[10px] uppercase">候选股票池配置</h3>
              <label className={`flex items-center gap-2 text-[10px] ${canToggleInheritScope ? "text-[#94a3b8] cursor-pointer" : "text-[#475569] cursor-not-allowed"}`}>
                <input
                  type="checkbox"
                  checked={inheritUpstreamScope}
                  disabled={!canToggleInheritScope}
                  onChange={(e) => updateForm("inherit_upstream_scope", e.target.checked)}
                  className="accent-[#0ea5e9]"
                />
                继承上游股票池
              </label>
            </div>
            {!canToggleInheritScope ? (
              <p className="text-[#64748b] text-[9px] mb-3">请先在上游触发配置中添加依赖模板，才能启用继承。</p>
            ) : inheritUpstreamScope ? (
              <p className="text-[#64748b] text-[9px] mb-3">已启用继承：股票池由上游模板驱动，当前模板中为只读展示。</p>
            ) : null}
            <StockPoolConfig
              type={stockPoolType}
              params={stockPoolParams}
              disabled={inheritUpstreamScope}
              onChange={(newType, newParams) => {
                setForm((prev) => ({
                  ...prev,
                  stock_pool_type: newType,
                  stock_pool_params: newParams as Record<string, unknown>,
                }));
              }}
            />
          </div>
        </div>

        <div className="bg-[#0f1115] border border-[var(--border)] rounded p-4">
          <h3 className="text-[#64748b] text-[10px] uppercase mb-3">Trigger: Schedule</h3>
          <div className="space-y-3">
            <div>
              <label className="text-[#64748b] text-[10px] block mb-1">Schedule Kind</label>
              <select
                value={form.schedule_kind || ""}
                onChange={(e) => {
                  const kind = e.target.value;
                  const nextCfg = normalizeScheduleConfig(
                    kind,
                    (form.schedule_config_json || {}) as Record<string, unknown>,
                  );
                  updateForm("schedule_kind", kind || undefined);
                  updateForm("schedule_config_json", nextCfg as Template["schedule_config_json"]);
                }}
                className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
              >
                <option value="">Manual only</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {scheduleKind === "daily" && (
              <>
                <div>
                  <label className="text-[#64748b] text-[10px] block mb-1">Time</label>
                  <input
                    type="time"
                    value={normalizeTime(scheduleConfig.time)}
                    onChange={(e) => updateScheduleConfig("time", e.target.value)}
                    className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs text-[#e2e8f0]">
                  <input
                    type="checkbox"
                    checked={scheduleConfig.trade_day_only !== false}
                    onChange={(e) => updateScheduleConfig("trade_day_only", e.target.checked)}
                    className="accent-[#0ea5e9]"
                  />
                  仅交易日触发
                </label>
              </>
            )}

            {scheduleKind === "weekly" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[#64748b] text-[10px] block mb-1">Weekday</label>
                    <select
                      value={Number(scheduleConfig.weekday || 1)}
                      onChange={(e) => updateScheduleConfig("weekday", Number(e.target.value))}
                      className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
                    >
                      {WEEKDAY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#64748b] text-[10px] block mb-1">Time</label>
                    <input
                      type="time"
                      value={normalizeTime(scheduleConfig.time)}
                      onChange={(e) => updateScheduleConfig("time", e.target.value)}
                      className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[#64748b] text-[10px] block mb-1">Cron (simplified)</label>
                  <input
                    type="text"
                    value={String(scheduleConfig.cron || buildWeeklyCron(normalizeTime(scheduleConfig.time), Number(scheduleConfig.weekday || 1)))}
                    onChange={(e) => updateScheduleConfig("cron", e.target.value)}
                    className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs font-mono"
                  />
                </div>
              </>
            )}

            {scheduleKind === "monthly" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[#64748b] text-[10px] block mb-1">Day Of Month</label>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={Number(scheduleConfig.monthday || 1)}
                      onChange={(e) => updateScheduleConfig("monthday", Number(e.target.value))}
                      className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-[#64748b] text-[10px] block mb-1">Time</label>
                    <input
                      type="time"
                      value={normalizeTime(scheduleConfig.time)}
                      onChange={(e) => updateScheduleConfig("time", e.target.value)}
                      className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[#64748b] text-[10px] block mb-1">Cron (simplified)</label>
                  <input
                    type="text"
                    value={String(scheduleConfig.cron || buildMonthlyCron(normalizeTime(scheduleConfig.time), Number(scheduleConfig.monthday || 1)))}
                    onChange={(e) => updateScheduleConfig("cron", e.target.value)}
                    className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs font-mono"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {isDailySync ? (
          <>
            <div className="bg-[#0f1115] border border-[var(--border)] rounded p-4">
              <h3 className="text-[#64748b] text-[10px] uppercase mb-3">日期范围 (Date Range)</h3>
              <DateRangeConfig
                type={dateRangeType}
                params={dateRangeParams}
                onChange={(newType, newParams) => {
                  setForm((prev) => ({
                    ...prev,
                    date_range_type: newType,
                    date_range_params: newParams as Record<string, unknown>,
                  }));
                }}
              />
            </div>
          </>
        ) : null}

        {/* Task Options Configuration */}
        {String(form.task_type || "").toLowerCase() === "quant_refresh" && (
          <div className="bg-[#0f1115] border border-[var(--border)] rounded p-4">
            <h3 className="text-[#64748b] text-[10px] uppercase mb-3">Task Options: 量化选股参数</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[#64748b] text-[10px] block mb-1">
                    选股结果数量 (limit)
                  </label>
                  <input
                    type="number"
                    min={10}
                    max={500}
                    value={Number((form.options_json as Record<string, unknown>)?.limit ?? 80)}
                    onChange={(e) => updateOptions("limit", Number(e.target.value))}
                    className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
                  />
                  <span className="text-[#64748b] text-[9px]">范围: 10-500</span>
                </div>
                <div>
                  <label className="text-[#64748b] text-[10px] block mb-1">
                    最低分数阈值 (min_score)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={Number((form.options_json as Record<string, unknown>)?.min_score ?? 45)}
                    onChange={(e) => updateOptions("min_score", Number(e.target.value))}
                    className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
                  />
                  <span className="text-[#64748b] text-[9px]">范围: 0-100</span>
                </div>
              </div>
              <div>
                <label className="text-[#64748b] text-[10px] block mb-1">
                  最大扫描股票数 (max_stocks)
                </label>
                <input
                  type="number"
                  min={100}
                  max={6000}
                  value={Number((form.options_json as Record<string, unknown>)?.max_stocks ?? 2800)}
                  onChange={(e) => updateOptions("max_stocks", Number(e.target.value))}
                  className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
                />
                <span className="text-[#64748b] text-[9px]">范围: 100-6000</span>
              </div>
            </div>
          </div>
        )}

        {String(form.task_type || "").toLowerCase() === "wyckoff_refresh" && (
          <div className="bg-[#0f1115] border border-[var(--border)] rounded p-4">
            <h3 className="text-[#64748b] text-[10px] uppercase mb-3">Task Options: 威科夫分析参数</h3>
            <div>
              <label className="text-[#64748b] text-[10px] block mb-1">
                最大分析股票数 (max_stocks)
              </label>
              <input
                type="number"
                min={0}
                max={10000}
                value={Number((form.options_json as Record<string, unknown>)?.max_stocks ?? 0)}
                onChange={(e) => updateOptions("max_stocks", Number(e.target.value))}
                className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
              />
              <span className="text-[#64748b] text-[9px]">0 表示分析全部股票</span>
            </div>
          </div>
        )}

        {String(form.task_type || "").toLowerCase() === "fundamentals_sync" && (
          <div className="bg-[#0f1115] border border-[var(--border)] rounded p-4">
            <h3 className="text-[#64748b] text-[10px] uppercase mb-3">Task Options</h3>
            <p className="text-[#64748b] text-xs">该任务无额外配置项，参数由系统自动处理。</p>
          </div>
        )}

      </div>
    </div>
  );
}
